import { createMarkup } from '@libs/utils';
import type { CertificateIcon } from '@models/graphql';
import { fixMarkupLinks } from '@libs/links.helper';
import classNames from 'classnames';
import styles from './ProductDescriptionTab.module.css';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Section } from '@product/Section/Section';
import type { ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';

type ProductDescriptionProps = {
  productDetails: string;
  product: ProductFragmentEnhanced;
  certificateIcons: CertificateIcon[];
  selectionProArguments?: string;
  selectionConArguments?: string;
};

export const ProductDescriptionTab = ({
  productDetails,
  product,
  certificateIcons,
  selectionProArguments,
  selectionConArguments,
}: ProductDescriptionProps) => {
  const { t } = useTranslation('product/productDetails');
  const { locale } = useRouter();

  const cfLifetimeTable = getAttributeValue<string>(product.attributes?.cf_lifetime_table as ProductAttribute<string>);

  return (
    <div className="p-4 md:p-0">
      <Section>
        <div className={styles.productDetails} dangerouslySetInnerHTML={createMarkup(productDetails)} />
        {cfLifetimeTable ? (
          <div
            className={classNames('mt-8', styles.cfLifetimeTable)}
            dangerouslySetInnerHTML={{
              __html: fixMarkupLinks(cfLifetimeTable ?? '', locale),
            }}
          />
        ) : null}
        {getAttributeValue<string[]>(product.attributes?.attr_certificates_Icons as ProductAttribute<string[]>)
          ?.length && (
          <>
            <div className="mt-12 text-2xl">{t('PRODUCT_DETAILS.CERTIFICATES_TITLE')}</div>
            <ul className="flex flex-row mt-4">
              {certificateIcons
                .filter(icon => icon.url)
                .map(icon => (
                  <li key={icon.key} className="mr-4">
                    <Image
                      unoptimized
                      data-testid="certificate-icon"
                      width={35}
                      height={35}
                      src={icon.url}
                      alt={icon.label}
                      title={icon.label}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                      }}
                    />
                  </li>
                ))}
            </ul>
          </>
        )}
      </Section>
      {selectionProArguments && (
        <div className="mt-16" data-testid="when-to-use-it-section">
          <Section title={t('PRODUCT_DETAILS.PRODUCT_SELECTION_PRO_ARGUMENTS_TITLE')}>
            <div
              className={classNames(styles.productDetails, styles.productSelectionArguments)}
              dangerouslySetInnerHTML={{
                __html: fixMarkupLinks(selectionProArguments, locale),
              }}
            />
          </Section>
        </div>
      )}
      {selectionConArguments && (
        <div className="mt-16" data-testid="when-not-to-use-it-section">
          <Section title={t('PRODUCT_DETAILS.PRODUCT_SELECTION_CON_ARGUMENTS_TITLE')}>
            <div
              className={classNames(styles.productDetails, styles.productSelectionArguments)}
              dangerouslySetInnerHTML={{ __html: fixMarkupLinks(selectionConArguments, locale) }}
            />
          </Section>
        </div>
      )}
    </div>
  );
};
